// 載入 manifest 與需要的分片，建立清單項目。分片依狀態選擇與日期範圍按需載入；
// 已載入的分片保留在記憶體（同一次瀏覽不重複下載）。
import type { IndexRow, Manifest } from '@dayone/core/snapshot';
import { taipeiEpochDay } from '@dayone/core/time';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildItems, type CatalogItem, neededShards } from '../filters/catalog.ts';
import type { FilterState } from '../filters/state.ts';
import { LoadError, type SnapshotClient } from './client.ts';

export type CatalogStatus = 'loading' | 'ready' | 'error';

export interface Catalog {
  status: CatalogStatus;
  error: LoadError | null;
  manifest: Manifest | null;
  items: CatalogItem[];
  /** 已有資料、正在載入額外分片（例如剛勾選已發售） */
  loadingMore: boolean;
  today: number;
  retry: () => void;
  /** 網址指定的遊戲（已套用合併轉向）：在所有已載入分片中查找，找不到時會載入其他分片 */
  game: { id: string; row: IndexRow | null; status: 'loading' | 'found' | 'missing' } | null;
}

const toLoadError = (e: unknown) =>
  e instanceof LoadError ? e : new LoadError('unknown', e instanceof Error ? e.message : String(e));

const splitKey = (key: string) => (key ? key.split(',') : []);

export function useCatalog(
  state: Pick<FilterState, 'status' | 'from' | 'to' | 'game'>,
  client: SnapshotClient,
): Catalog {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [shards, setShards] = useState<ReadonlyMap<string, IndexRow[]>>(() => new Map());
  const shardsRef = useRef(shards);
  shardsRef.current = shards;
  const [error, setError] = useState<LoadError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const today = useMemo(() => taipeiEpochDay(Date.now()), []);
  const currentYear = useMemo(() => new Date(Date.now() + 8 * 3600_000).getUTCFullYear(), []);

  // manifest；attempt 改變（按下重試）時重新取得
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt 是重試的觸發器
  useEffect(() => {
    let alive = true;
    client.manifest().then(
      (m) => alive && setManifest(m),
      (e) => alive && setError(toLoadError(e)),
    );
    return () => {
      alive = false;
    };
  }, [client, attempt]);

  const { status, from, to } = state;
  // 清單需要的分片（依狀態選擇與日期範圍）
  const baseKey = useMemo(
    () => (manifest ? neededShards(manifest, { status, from, to }, currentYear).join(',') : ''),
    [manifest, status, from, to, currentYear],
  );

  // 網址指定的遊戲：先套用合併轉向，再到所有已載入的分片找
  const gameId = state.game ? (manifest?.redirects[state.game] ?? state.game) : null;
  const gameRow = useMemo(() => {
    if (!gameId) return null;
    for (const rows of shards.values()) {
      const r = rows.find((x) => x.id === gameId);
      if (r) return r;
    }
    return null;
  }, [gameId, shards]);
  const allIds = manifest?.shards.map((s) => s.id) ?? [];
  const baseLoaded = splitKey(baseKey).every((id) => shards.has(id));
  const allLoaded = allIds.every((id) => shards.has(id));
  // 例如只看未發售時開啟已發售遊戲的連結：清單需要的分片載完仍找不到，就載入其餘分片
  const needAll = !!gameId && !gameRow && baseLoaded && !allLoaded;
  const wantedKey = needAll ? allIds.join(',') : baseKey;

  // 只補缺少的分片；已載入的不重複下載
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt 是重試的觸發器
  useEffect(() => {
    if (!manifest) return;
    const missing = splitKey(wantedKey).filter((id) => !shardsRef.current.has(id));
    if (missing.length === 0) return;
    let alive = true;
    const files = new Map(manifest.shards.map((s) => [s.id, s.file]));
    Promise.all(missing.map(async (id) => [id, await client.shard(files.get(id) as string)] as const)).then(
      (loaded) => {
        if (!alive) return;
        setShards((prev) => {
          const next = new Map(prev);
          for (const [id, rows] of loaded) next.set(id, rows);
          return next;
        });
      },
      (e) => alive && setError(toLoadError(e)),
    );
    return () => {
      alive = false;
    };
  }, [manifest, wantedKey, client, attempt]);

  // 清單只用「狀態選擇需要的」分片建立項目（為了找單款遊戲而多載的分片不影響清單）；
  // 取消勾選已發售後不顯示，但資料留在記憶體供再次勾選
  const wanted = splitKey(baseKey);
  const loadedKey = wanted.filter((id) => shards.has(id)).join(',');
  const items = useMemo(
    () =>
      buildItems(
        splitKey(loadedKey).flatMap((id) => shards.get(id) ?? []),
        today,
      ),
    [loadedKey, shards, today],
  );

  const retry = useCallback(() => {
    setError(null);
    setAttempt((n) => n + 1);
  }, []);

  const loadedCount = splitKey(loadedKey).length;
  const hasAll = !!manifest && loadedCount === wanted.length;
  // 已經有部分資料時維持顯示（錯誤以提示條呈現），沒有任何資料時才整頁顯示錯誤
  const hasData = !!manifest && (loadedCount > 0 || wanted.length === 0);
  return {
    status: hasData ? 'ready' : error ? 'error' : 'loading',
    error,
    manifest,
    items,
    loadingMore: hasData && !hasAll && !error,
    today,
    retry,
    game: gameId
      ? { id: gameId, row: gameRow, status: gameRow ? 'found' : manifest && allLoaded ? 'missing' : 'loading' }
      : null,
  };
}
